import type { IActiveAiScriptRun } from "@/payroll/ai-script/types";
import type {
  PayBreakdownPeriod,
  PayBreakdownRerunAction,
  PayBreakdownRunTarget,
} from "@/payroll/pay-breakdown/types";
import { PAY_BREAKDOWN_RUN_TARGET_TYPE } from "@/payroll/pay-breakdown/types";
import { LOCKED_RERUN_REASON } from "@/payroll/pay-periods/pay-period-lock";
import type { EnrichedPayPeriod, PayrollRunStatus } from "@/payroll/payroll.d";
import { canTriggerNewRun } from "@/payroll/utils/period-helpers";

export interface GetPayBreakdownRunTargetParams {
  selectedPeriod: PayBreakdownPeriod | undefined;
  selectedPeriodForCard: EnrichedPayPeriod | undefined;
  latestIcmRunStatus: PayrollRunStatus | null;
  hasActiveRun: boolean;
  isLocked: boolean;
  isFuturePeriod?: boolean;
  hasAiScript: boolean;
  hasIcmAccess: boolean;
  hasPayrollScript: boolean;
  icmRunDisabledReason: string | undefined;
  canRerunWholePayroll: boolean;
  canStartInitialPayroll?: boolean;
  hasRerunUnits?: boolean;
}

export const getPayBreakdownRunTarget = ({
  selectedPeriod,
  selectedPeriodForCard,
  latestIcmRunStatus,
  hasActiveRun,
  isLocked,
  isFuturePeriod = false,
  hasAiScript,
  hasIcmAccess,
  hasPayrollScript,
  icmRunDisabledReason,
  canRerunWholePayroll,
  canStartInitialPayroll = false,
  hasRerunUnits = false,
}: GetPayBreakdownRunTargetParams): PayBreakdownRunTarget => {
  const activeRunDisabledReason = hasActiveRun
    ? "Payroll is already running for this period"
    : undefined;
  const disabledReason = isFuturePeriod
    ? "Payroll can run after this period starts."
    : isLocked
      ? LOCKED_RERUN_REASON
      : activeRunDisabledReason;

  if (hasAiScript) {
    const canAccessRerun =
      !!selectedPeriod &&
      !isFuturePeriod &&
      (canRerunWholePayroll || hasRerunUnits || canStartInitialPayroll);

    return {
      type: PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript,
      canAccessRerun,
      canRunWholePayroll: canRerunWholePayroll && !isFuturePeriod,
      canRunPeriod: canAccessRerun && !isLocked && !hasActiveRun,
      disabledReason,
    };
  }

  if (hasIcmAccess) {
    const canRunIcmPeriod =
      !!selectedPeriodForCard &&
      !icmRunDisabledReason &&
      (!selectedPeriodForCard.existsInDb ||
        canTriggerNewRun(selectedPeriodForCard, latestIcmRunStatus));

    return {
      type: PAY_BREAKDOWN_RUN_TARGET_TYPE.icm,
      canRunPeriod:
        !isFuturePeriod && !isLocked && !hasActiveRun && canRunIcmPeriod,
      disabledReason: disabledReason ?? icmRunDisabledReason,
    };
  }

  if (hasPayrollScript) {
    return {
      type: PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript,
      canRunPeriod: false,
      disabledReason,
    };
  }

  return { type: PAY_BREAKDOWN_RUN_TARGET_TYPE.none, canRunPeriod: false };
};

interface GetPayBreakdownRerunActionParams {
  runTarget: PayBreakdownRunTarget;
  hasActiveRun: boolean;
  showIcmRerunControls: boolean;
  isCreatingAiRun: boolean;
  isCreatingPeriod: boolean;
  isTriggeringRun: boolean;
  isIcmRunActionRunning: boolean;
  onRunPreferredPayroll: () => void;
}

export const getPayBreakdownRerunAction = ({
  runTarget,
  hasActiveRun,
  showIcmRerunControls,
  isCreatingAiRun,
  isCreatingPeriod,
  isTriggeringRun,
  isIcmRunActionRunning,
  onRunPreferredPayroll,
}: GetPayBreakdownRerunActionParams): PayBreakdownRerunAction => {
  switch (runTarget.type) {
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript:
      return {
        isVisible: runTarget.canAccessRerun,
        onRun: onRunPreferredPayroll,
        canRunPeriod: runTarget.canRunPeriod,
        isRunning: isCreatingAiRun || hasActiveRun,
        disabledReason: runTarget.disabledReason,
      };
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript:
      return {
        isVisible: false,
        onRun: onRunPreferredPayroll,
        canRunPeriod: runTarget.canRunPeriod,
        isRunning: isCreatingPeriod || isTriggeringRun || hasActiveRun,
        disabledReason: runTarget.disabledReason,
      };
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.icm:
      return {
        isVisible: showIcmRerunControls,
        onRun: onRunPreferredPayroll,
        canRunPeriod: runTarget.canRunPeriod,
        isRunning: isIcmRunActionRunning,
        disabledReason: runTarget.disabledReason,
      };
    case PAY_BREAKDOWN_RUN_TARGET_TYPE.none:
      return {
        isVisible: false,
        onRun: onRunPreferredPayroll,
        canRunPeriod: false,
        isRunning: false,
        disabledReason: runTarget.disabledReason,
      };
  }
};

export const isFullWorkbookRun = (run: IActiveAiScriptRun): boolean =>
  run.sheet_scope == null;

export const formatList = (items: string[]): string => {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];

  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};
